import React, { useEffect, useRef, useState } from 'react';
import { fmt, fmtK, money } from './format';
import { getCategoryMeta } from './categoryMeta';

export function Donut({ income, expenses, size = 128, sw = 16 }) {
  const total = income + expenses || 1;
  const r = (size - sw) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const eArc = (expenses / total) * circ;
  const iArc = (income / total) * circ;
  const net = income - expenses;
  const incomePct = Math.round((income / total) * 100);
  const expensePct = Math.round((expenses / total) * 100);
  const ariaLabel = `Monthly summary: ${incomePct}% income (${fmt(income)}), ${expensePct}% expenses (${fmt(expenses)}), net ${fmt(net)}`;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={ariaLabel}>
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={sw} />
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="var(--neg)"
        strokeWidth={sw}
        strokeDasharray={`${eArc} ${circ - eArc}`}
        strokeDashoffset={circ * 0.25}
        strokeLinecap="round"
      />
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="var(--pos)"
        strokeWidth={sw}
        strokeDasharray={`${iArc} ${circ - iArc}`}
        strokeDashoffset={circ * 0.25 - eArc}
        strokeLinecap="round"
      />
      <text x={c} y={c - 4} textAnchor="middle" fontSize="11" fill="var(--ink-3)" fontWeight="600" fontFamily="var(--fontUI)">
        Net
      </text>
      <text
        x={c}
        y={c + 16}
        textAnchor="middle"
        fontSize="18"
        fontWeight="700"
        fill={net < 0 ? 'var(--neg)' : 'var(--ink)'}
        fontFamily="var(--fontNum)"
      >
        {fmtK(net)}
      </text>
    </svg>
  );
}

export function ForecastChart({ data, overlays = [], bands = null, height = 150 }) {
  const [hover, setHover] = useState(null);
  const containerRef = useRef(null);
  // The viewBox width must track the actual rendered pixel width. Height is a
  // literal number in both the `height` prop and viewBox, so vertical scale is
  // always 1:1 — but width stretches to fill "100%" of the container, and if
  // the viewBox width doesn't match that container width, the horizontal scale
  // factor drifts from 1 and every circle marker renders as an ellipse.
  const [measuredWidth, setMeasuredWidth] = useState(600);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setMeasuredWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const W = measuredWidth;
  const H = height;
  const padX = 8;
  const padTop = 18;
  const padBot = 26;
  if (!data?.length) return null;
  const xs = data.map((_, i) => padX + (i * (W - 2 * padX)) / (data.length - 1 || 1));

  // bands is an array aligned with data: [{p10, p50, p90, ...}]. When the
  // points carry no real spread (e.g. low-confidence -> p10 === p90), we
  // treat it as no-band rather than drawing a zero-height ribbon.
  const hasBands = Array.isArray(bands)
    && bands.length === data.length
    && bands.some((b) => Number.isFinite(b?.p10) && Number.isFinite(b?.p90) && b.p10 !== b.p90);

  // Shared y-axis across baseline + overlays + bands so everything is comparable.
  const allVals = [
    ...data.map((p) => p.balance),
    ...overlays.flatMap((o) => (o?.data || []).map((p) => p.balance)),
    ...(hasBands ? bands.flatMap((b) => [b.p10, b.p90]) : []),
  ];
  const dataMin = Math.min(...allVals);
  const dataMax = Math.max(...allVals);

  // When a scenario overlay dwarfs the baseline, a shared *linear* axis crushes
  // the smaller baseline into a flat line. Switch to a log axis in that case so
  // both series keep a readable slope. Only valid when everything is positive.
  const baselineMax = Math.max(...data.map((p) => p.balance));
  const overlayMax = overlays.length
    ? Math.max(baselineMax, ...overlays.flatMap((o) => (o?.data || []).map((p) => p.balance)))
    : baselineMax;
  const allPositive = allVals.every((v) => Number.isFinite(v) && v > 0);
  const useLog = allPositive && overlayMax / (baselineMax || 1) >= 3;

  const min = useLog ? dataMin : Math.min(dataMin, 0);
  const max = dataMax;
  const range = max - min || 1;
  const tv = (v) => (useLog ? Math.log10(Math.max(v, 1e-6)) : v);
  const lo = tv(min);
  const span = (tv(max) - lo) || 1;
  const y = (v) => padTop + (1 - (tv(v) - lo) / span) * (H - padTop - padBot);

  const buildSmoothPath = (series) => series
    .map((p, i) => {
      if (i === 0) return `M${p.x},${p.y}`;
      const prev = series[i - 1];
      const cx = (prev.x + p.x) / 2;
      return `C${cx},${prev.y} ${cx},${p.y} ${p.x},${p.y}`;
    })
    .join(' ');

  const pts = data.map((p, i) => ({ x: xs[i], y: y(p.balance), ...p }));
  const overlayLines = overlays
    .filter((o) => o && Array.isArray(o.data) && o.data.length === data.length)
    .map((o) => ({
      ...o,
      pts: o.data.map((p, i) => ({ x: xs[i], y: y(p.balance), ...p })),
    }));

  const line = buildSmoothPath(pts);
  const area = `${line} L${pts[pts.length - 1].x},${H - padBot} L${pts[0].x},${H - padBot} Z`;
  const zeroY = y(0);
  const sel = hover != null ? pts[hover] : pts[pts.length - 1];

  // Horizontal gridlines at "nice" dollar values so the axis is readable.
  const gridTicks = [];
  if (useLog) {
    // 1×/3× steps per decade (…$1k, $3k, $10k, $30k, $100k…).
    const e0 = Math.floor(Math.log10(min));
    const e1 = Math.ceil(Math.log10(max));
    for (let e = e0; e <= e1 && gridTicks.length < 6; e += 1) {
      for (const m of [1, 3]) {
        const v = m * 10 ** e;
        if (v >= min && v <= max && gridTicks.length < 6) gridTicks.push(v);
      }
    }
  } else {
    const rawStep = range / 3;
    const mag = 10 ** Math.floor(Math.log10(rawStep || 1));
    const norm = rawStep / mag;
    const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
    for (let v = Math.ceil(min / step) * step; v <= max && gridTicks.length < 6; v += step) {
      gridTicks.push(v);
    }
  }

  // Values under the cursor: the baseline balance plus each scenario overlay's
  // projected balance at the same month, so the dashed lines become readable.
  const frac = sel ? sel.x / W : 0;
  const tipRows = hover != null
    ? [
      { name: 'Baseline', color: 'var(--accent)', balance: pts[hover]?.balance, dashed: false },
      ...overlayLines.map((o) => ({
        name: o.name,
        color: o.color || 'var(--ink-2)',
        balance: o.pts[hover]?.balance,
        dashed: true,
      })),
    ]
    : [];

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      preserveAspectRatio="none"
      onMouseLeave={() => setHover(null)}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <linearGradient id="fcFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.28" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {gridTicks.map((v) => (
        <line
          key={`grid-${v}`}
          x1={padX}
          x2={W - padX}
          y1={y(v)}
          y2={y(v)}
          stroke="var(--line)"
          strokeWidth="1"
          opacity="0.5"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {min < 0 && (
        <line
          x1={padX}
          x2={W - padX}
          y1={zeroY}
          y2={zeroY}
          stroke="var(--neg)"
          strokeWidth="1"
          strokeDasharray="3 4"
          opacity="0.5"
        />
      )}
      <path d={area} fill="url(#fcFill)" />
      {hasBands && (() => {
        const p10pts = bands.map((b, i) => ({ x: xs[i], y: y(b.p10) }));
        const p90pts = bands.map((b, i) => ({ x: xs[i], y: y(b.p90) }));
        const top = buildSmoothPath(p90pts);
        const bottomReversed = [...p10pts].reverse();
        const bottom = buildSmoothPath(bottomReversed).replace(/^M/, 'L');
        const bandArea = `${top} ${bottom} Z`;
        return (
          <>
            <path d={bandArea} fill="var(--accent)" opacity="0.13" />
            <path d={buildSmoothPath(p10pts)} fill="none" stroke="var(--accent)" strokeWidth="1" strokeDasharray="2 3" opacity="0.45" vectorEffect="non-scaling-stroke" />
            <path d={buildSmoothPath(p90pts)} fill="none" stroke="var(--accent)" strokeWidth="1" strokeDasharray="2 3" opacity="0.45" vectorEffect="non-scaling-stroke" />
          </>
        );
      })()}
      <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
      {overlayLines.map((o, idx) => (
        <path
          key={o.id || idx}
          d={buildSmoothPath(o.pts)}
          fill="none"
          stroke={o.color || 'var(--ink-2)'}
          strokeWidth="2"
          strokeDasharray="5 4"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          opacity="0.85"
        />
      ))}
      {pts.map((p, i) => (
        <g key={i}>
          <rect
            x={p.x - W / data.length / 2}
            y={0}
            width={W / data.length}
            height={H}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            style={{ cursor: 'pointer' }}
          />
          <circle cx={p.x} cy={p.y} r={hover === i ? 5 : 0} fill="var(--accent)" stroke="var(--surface)" strokeWidth="2" />
        </g>
      ))}
      {sel && (
        <g>
          <line x1={sel.x} x2={sel.x} y1={padTop - 6} y2={H - padBot} stroke="var(--line-strong)" strokeWidth="1" />
          <circle cx={sel.x} cy={sel.y} r="4.5" fill="var(--accent)" stroke="var(--surface)" strokeWidth="2" />
        </g>
      )}
    </svg>
      {gridTicks.map((v) => (
        <div
          key={`gridlbl-${v}`}
          style={{
            position: 'absolute',
            top: y(v),
            left: padX,
            transform: 'translateY(-100%)',
            pointerEvents: 'none',
            fontFamily: 'var(--fontUI)',
            fontSize: 10,
            lineHeight: 1,
            color: 'var(--ink-3)',
          }}
        >
          {fmtK(v)}
        </div>
      ))}
      {pts.map((p, i) => {
        const show = i % 2 === 0 || hover === i;
        if (!show) return null;
        const isLast = i === pts.length - 1;
        const active = hover === i || (hover == null && isLast);
        return (
          <div
            key={`mo-${i}`}
            style={{
              position: 'absolute',
              top: H - 16,
              left: `${(p.x / W) * 100}%`,
              transform: i === 0 ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
              pointerEvents: 'none',
              fontFamily: 'var(--fontUI)',
              fontSize: 11,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              color: active ? 'var(--ink)' : 'var(--ink-3)',
              fontWeight: hover === i ? 600 : 500,
            }}
          >
            {p.label}
          </div>
        );
      })}
      {useLog && (
        <div
          style={{
            position: 'absolute',
            top: 2,
            right: 4,
            pointerEvents: 'none',
            fontFamily: 'var(--fontUI)',
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: '0.02em',
          }}
        >
          log scale
        </div>
      )}
      {tipRows.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: `${frac * 100}%`,
            transform: frac > 0.6 ? 'translateX(calc(-100% - 8px))' : 'translateX(8px)',
            pointerEvents: 'none',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: '8px 10px',
            fontFamily: 'var(--fontUI)',
            fontSize: 12,
            whiteSpace: 'nowrap',
            minWidth: 150,
            zIndex: 2,
          }}
        >
          <div className="title" style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>{sel.label}</div>
          {tipRows.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: i === 0 ? 0 : 3 }}>
              <span
                style={{
                  width: 12,
                  height: 0,
                  flex: '0 0 auto',
                  borderTop: `2px ${r.dashed ? 'dashed' : 'solid'} ${r.color}`,
                }}
              />
              <span style={{ color: 'var(--ink-3)' }}>{r.name}</span>
              <span style={{ marginLeft: 'auto', fontWeight: 600, color: 'var(--ink)' }}>
                {Number.isFinite(r.balance) ? money(r.balance, { cents: false }) : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CategoryBars({ rows, limit = 5 }) {
  const top = rows.slice(0, limit);
  const m = top[0]?.amt || 1;
  const ariaLabel = top.length
    ? `Top spending categories: ${top.map((r) => `${r.cat} ${fmt(r.amt)}`).join(', ')}`
    : 'No spending categories';
  return (
    <div role="img" aria-label={ariaLabel} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {top.map((r) => {
        const c = getCategoryMeta(r.cat).tint;
        return (
          <div key={r.cat}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
              <span className="title" style={{ color: 'var(--ink-2)', fontWeight: 600 }}>{r.cat}</span>
              <span className="num" style={{ color: 'var(--ink)', fontWeight: 600 }}>
                {money(r.amt)}
              </span>
            </div>
            <div style={{ height: 8, borderRadius: 99, background: 'var(--surface-3)', overflow: 'hidden' }}>
              <div className="rise" style={{ height: '100%', borderRadius: 99, width: `${(r.amt / m) * 100}%`, background: c }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Daily cash ledger over the runway horizon. Deliberately a separate chart from
// ForecastChart rather than a mode of it: this is day-granular with a threshold
// line and a breach marker, where that one is month-granular with scenario
// overlays and a log-axis escape hatch. Folding both into one component would
// mean a prop matrix where most combinations are meaningless.
//
// One series (expected balance) plus its own uncertainty band and a reference
// line — not a categorical palette, so identity never rides on hue: the band is
// the same hue as the line it belongs to, and the floor is a status color
// carrying a text label, never color alone.
export function RunwayChart({ days, floor = 0, breachKey = null, height = 170 }) {
  const [hover, setHover] = useState(null);
  const containerRef = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(600);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setMeasuredWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!days?.length) return null;

  const W = measuredWidth;
  const H = height;
  const padX = 8;
  const padTop = 16;
  const padBot = 24;

  const xs = days.map((_, i) => padX + (i * (W - 2 * padX)) / (days.length - 1 || 1));

  // The floor is part of the y-domain: a chart that clips the very threshold it
  // is about would hide the thing the user came to see.
  const vals = [...days.map((d) => d.balance), ...days.map((d) => d.pessimistic), floor];
  const dataMin = Math.min(...vals);
  const dataMax = Math.max(...vals);
  const min = Math.min(dataMin, 0);
  const max = dataMax;
  const span = (max - min) || 1;
  const y = (v) => padTop + (1 - (v - min) / span) * (H - padTop - padBot);

  const path = (sel) => days
    .map((d, i) => `${i ? 'L' : 'M'}${xs[i]},${y(sel(d))}`)
    .join(' ');

  const expected = path((d) => d.balance);

  // Band runs out along the pessimistic line and back along the expected one,
  // so it reads as spread around that series rather than a second area. The
  // return leg must walk indices backwards — traversing forward on both legs
  // makes the outline self-intersect and fill as crossing wedges.
  let back = '';
  for (let i = days.length - 1; i >= 0; i -= 1) back += `L${xs[i]},${y(days[i].balance)} `;
  const band = `${path((d) => d.pessimistic)} ${back.trim()} Z`;

  const hasSpread = days.some((d) => d.pessimistic !== d.balance);
  const floorY = y(floor);
  const breachIdx = breachKey ? days.findIndex((d) => d.key === breachKey) : -1;
  const sel = hover != null ? hover : (breachIdx >= 0 ? breachIdx : null);
  const selDay = sel != null ? days[sel] : null;

  // Sparse date ticks — a label every ~2 weeks, never one per day.
  const tickStep = Math.max(1, Math.round((days.length - 1) / 4));
  const ticks = days
    .map((d, i) => ({ d, i }))
    .filter(({ i }) => i % tickStep === 0 || i === days.length - 1);

  const dayText = (d) => d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // Index 0 is today's anchor and never carries events, so it can't appear here.
  const cardPaymentIdxs = days
    .map((d, i) => (d.events?.some((e) => e.isCardPayment) ? i : -1))
    .filter((i) => i > 0);

  const summary = breachIdx >= 0
    ? `Daily cash projection. Balance is projected to fall below ${fmt(floor, { cents: false })} on ${dayText(days[breachIdx])}.`
    : `Daily cash projection over ${days.length - 1} days. Balance stays above ${fmt(floor, { cents: false })} throughout. Low point ${fmt(Math.min(...days.map((d) => d.balance)), { cents: false })}.`;

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / (rect.width || 1);
    const idx = Math.round(frac * (days.length - 1));
    setHover(Math.max(0, Math.min(days.length - 1, idx)));
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <svg
        role="img"
        aria-label={summary}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        preserveAspectRatio="none"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ display: 'block', overflow: 'visible', touchAction: 'pan-y' }}
      >
        {hasSpread && <path d={band} fill="var(--accent)" opacity="0.13" />}

        {/* Floor first, so the balance line reads on top of it. */}
        <line
          x1={padX} x2={W - padX} y1={floorY} y2={floorY}
          stroke="var(--neg)" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.75"
        />

        <path d={expected} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" />

        {/* Card payments are the largest single outflows most people have, and
            unlike a bill their size is a consequence of last month's spending.
            Marking them lets you see which drop is the card before hovering.
            Drawn before the breach marker so a breach caused BY a card payment
            still reads as a breach first. */}
        {cardPaymentIdxs.map((i) => (
          <circle
            key={`card-${days[i].key}`}
            data-testid="card-payment-marker"
            cx={xs[i]} cy={y(days[i].balance)} r="3.5"
            fill="var(--surface)" stroke="var(--accent)" strokeWidth="2"
          />
        ))}

        {breachIdx >= 0 && (
          <>
            <line
              x1={xs[breachIdx]} x2={xs[breachIdx]} y1={padTop} y2={H - padBot}
              stroke="var(--neg)" strokeWidth="1.5" opacity="0.55"
            />
            {/* 2px surface ring so the marker stays legible over the band. */}
            <circle
              cx={xs[breachIdx]} cy={y(days[breachIdx].balance)} r="5"
              fill="var(--neg)" stroke="var(--surface)" strokeWidth="2"
            />
          </>
        )}

        {hover != null && (
          <line
            x1={xs[hover]} x2={xs[hover]} y1={padTop} y2={H - padBot}
            stroke="var(--ink-3)" strokeWidth="1" opacity="0.5"
          />
        )}

        {/* Painted last, after every line: the surface-colored halo only masks
            marks drawn before it, and the balance line crosses this label
            whenever the balance passes near the floor — which is exactly the
            case the label exists for. */}
        <text
          x={padX + 2} y={floorY - 6} fontSize="10" fontWeight="700"
          fill="var(--neg)" opacity="0.95"
          stroke="var(--surface)" strokeWidth="3.5" paintOrder="stroke"
        >
          {fmt(floor, { cents: false })} floor
        </text>

        {ticks.map(({ d, i }) => (
          <text
            key={d.key}
            x={Math.min(Math.max(xs[i], 14), W - 14)}
            y={H - 8}
            fontSize="10"
            textAnchor="middle"
            fill="var(--ink-3)"
          >
            {dayText(d)}
          </text>
        ))}
      </svg>

      {selDay && (
        <div style={{
          marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
          fontSize: 12, color: 'var(--ink-2)',
        }}>
          <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{dayText(selDay)}</span>
          <span className="num" style={{ fontWeight: 600, color: selDay.balance < floor ? 'var(--neg)' : 'var(--ink)' }}>
            {money(selDay.balance)}
          </span>
          {selDay.events.length > 0 && (
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
              {selDay.events.map((e, i) => (
                <span
                  key={`${e.seriesKey}-${i}`}
                  style={{
                    color: e.isCardPayment ? 'var(--ink-2)' : 'var(--ink-3)',
                    fontWeight: e.isCardPayment ? 600 : 400,
                  }}
                >
                  {e.delta < 0 ? '−' : '+'}
                  {money(Math.abs(e.delta), { cents: false })} {e.description}
                  {e.inFlight && (
                    <span style={{ color: 'var(--accent)', fontWeight: 600 }}> · settling</span>
                  )}
                  {e.planned && ' · planned'}
                  {e.missed && ' · not seen yet'}
                </span>
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The one chart on the Today page. Deliberately minimal: no axis box, no
 * gridline labels, no pessimistic band, no breach markers, no tooltip.
 *
 * A separate component rather than four more props on RunwayChart — the two
 * charts answer different questions ("am I about to overdraft", with all the
 * apparatus that needs, vs "what does the next month look like"), and the
 * apparatus is exactly what this one is removing.
 */
export function CashLine({ days, height = 130, compact = false, depositDate = null }) {
  const containerRef = useRef(null);
  // Same measurement as ForecastChart, and for the same reason: this drew into
  // a fixed 960-wide viewBox stretched with preserveAspectRatio="none", so on a
  // phone the horizontal scale was ~0.35 against a vertical ~0.8 — the low-point
  // marker rendered as a squashed ellipse and the stroke thinned on every
  // vertical run. The viewBox width has to track the real pixel width.
  const [measuredWidth, setMeasuredWidth] = useState(600);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setMeasuredWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = compact ? 104 : height;
  const R = 5;                  // low-point marker radius
  const TOP = 14;
  const BASE = H - 12;          // baseline gridline; the area closes to it
  const W = measuredWidth;

  const n = days?.length || 0;
  const balances = (days || []).map((d) => d.balance);
  const min = n ? Math.min(...balances) : 0;
  const max = n ? Math.max(...balances) : 0;
  const span = (max - min) || 1;
  const y = (v) => TOP + (1 - (v - min) / span) * (BASE - TOP);
  // Inset by the marker radius so a low point on day 0 or the last day draws a
  // whole circle instead of a half one clipped by the viewBox edge.
  const xs = (days || []).map((_, i) => R + (i * (W - 2 * R)) / (n - 1 || 1));

  const lowIdx = balances.indexOf(min);
  // The hero's deposit, not a fresh scan for any positive delta — this marker
  // has to name the same day the "out before payday" list ends on.
  const depositIdx = depositDate
    ? (days || []).findIndex((d) => d.date.getTime() === depositDate.getTime())
    : -1;

  const pctAt = (i) => (xs[i] / W) * 100;
  const lowPct = n ? pctAt(lowIdx) : 0;
  const depositPct = depositIdx > 0 ? pctAt(depositIdx) : null;

  const line = (days || [])
    .map((d, i) => `${i ? 'L' : 'M'}${xs[i].toFixed(1)},${y(d.balance).toFixed(1)}`)
    .join(' ');
  const area = `${line} L${xs[n - 1]},${BASE} L${xs[0]},${BASE} Z`;

  // The two inner labels are anchored away from each other rather than both
  // centred: centred, they collide whenever their days are close together.
  // If they'd still overlap, the deposit label goes — the payroll row in the
  // left column already carries that date.
  const showDeposit = depositPct != null && depositPct - lowPct > (lowPct < 25 ? 22 : 9) && depositPct < 88;
  // A low point in the first few days can't carry a right-anchored label —
  // it would run off the left edge and sit on top of "Today". Flip it to the
  // other side of the marker in that case, and drop "Today" when the flipped
  // label starts on top of it.
  const showLow = lowIdx > 0;
  const lowFlipped = lowPct < 25;
  const showToday = !showLow || !lowFlipped || lowPct > 11;
  const dateAt = (i) => days[i].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // After the hooks, never before them.
  if (n < 2) return null;

  // The low point is the one thing on this chart the user can act on, so it
  // follows the page's colour policy: alarm when the projection goes under,
  // plain accent when it doesn't. Leaving it accent-coloured put a calm marker
  // next to the alarm-coloured figure naming the same number.
  const lowStroke = min < 0 ? 'var(--alarm)' : 'var(--accent)';

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        style={{ display: 'block', width: '100%' }}
        role="img"
        aria-label={`Projected cash balance over the next ${n - 1} days; low point of ${fmt(min, { cents: false })} on ${dateAt(lowIdx)}`}
      >
        <line x1="0" x2={W} y1={(TOP + BASE) / 2} y2={(TOP + BASE) / 2} stroke="var(--line)" strokeWidth="1" />
        <line x1="0" x2={W} y1={BASE} y2={BASE} stroke="var(--line)" strokeWidth="1" />
        <path d={area} fill="var(--accent)" opacity="0.07" />
        <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinejoin="round" />
        <circle cx={xs[lowIdx]} cy={y(min)} r={R} fill="var(--bg)" stroke={lowStroke} strokeWidth="2.5" />
      </svg>
      {/* Positioned against the same x coordinates as the path — distributing
          these with space-between would point "low" at the wrong day. */}
      <div style={{ position: 'relative', height: 16, fontSize: 11.5, color: 'var(--ink-3)' }}>
        {showToday && <span style={{ position: 'absolute', left: 0, top: 0 }}>Today</span>}
        {showLow && (
          <span style={{
            position: 'absolute', left: `${lowPct}%`, top: 0,
            transform: lowFlipped ? undefined : 'translateX(-100%)',
            paddingLeft: lowFlipped ? 7 : undefined,
            paddingRight: lowFlipped ? undefined : 7,
            whiteSpace: 'nowrap', color: 'var(--ink)', fontWeight: 600,
          }}>
            {dateAt(lowIdx)} · low
          </span>
        )}
        {showDeposit && (
          <span style={{ position: 'absolute', left: `${depositPct}%`, top: 0, paddingLeft: 3, whiteSpace: 'nowrap' }}>
            {dateAt(depositIdx)} · deposit
          </span>
        )}
        <span style={{ position: 'absolute', right: 0, top: 0 }}>{dateAt(n - 1)}</span>
      </div>
    </div>
  );
}
